import { apiFetch } from '@m/utils/http';
import { createEffect, createEvent } from 'effector';

import type {
  ExistingSubscriptions,
  NewSubscriptionCheckoutResponse,
  SelfServiceMembership,
  Subscription,
} from '@m/types/api';

/**
 * events
 */
export const removeSubscriptionFromCart = createEvent();
export const resetSubscriptionAsyncStatuses = createEvent();

/**
 * effects
 */

export const addSubscriptionToCart = createEffect({
  handler: ({ planUuid }: { planUuid: string }) =>
    apiFetch<NewSubscriptionCheckoutResponse>(
      `/v2/subscription/subscription-plan/${planUuid}/prepare-checkout`,
      {
        method: 'POST',
      },
    ),
});

export const addLegacySubscriptionToCart = createEffect({
  handler: ({
    planId,
    startDate,
  }: {
    planId: number;
    /** Optional start date in ISO 8601 format e.g. "2024-01-15T10:30:00Z" */
    startDate?: string;
  }) =>
    apiFetch<{ subscription: Subscription }>(`/customer/subscription-plan/checkout/${planId}`, {
      method: 'POST',
      ...(startDate ? { params: { startDate } } : {}),
    }),
});

export const getActiveSubscriptions = createEffect({
  handler: () => apiFetch<ExistingSubscriptions[]>('/customer/subscription'),
});

export const getSiteSubscriptions = createEffect({
  handler: ({ siteId }: { siteId: number }) =>
    apiFetch(`/customer/subscription/plan/site/${siteId}`),
});

export const getAvailableAndActiveSubscriptions = createEffect({
  handler: async ({
    latitude,
    longitude,
    maxDistanceMiles,
  }: {
    latitude: number;
    longitude: number;
    maxDistanceMiles: number;
  }) => {
    const response = await apiFetch('/subscription/search', {
      // @ts-ignore ts-migrate(2322) FIXME: Type '{ latitude: number; longitude: number; maxDi...
      params: {
        latitude,
        longitude,
        maxDistanceMiles,
      },
    });

    if (response && response.success) {
      return response;
    }
    throw new Error('Could not get available and active subscriptions');
  },
});

export const makePaymentForSubscription = createEffect({
  handler: async (
    parameters:
      | { paymentMethodId: number; selfServiceSubscriptionId: string }
      | { paymentMethodId: number; subscriptionId: number },
  ) => {
    let url;
    let body;

    if ('selfServiceSubscriptionId' in parameters) {
      url = `/v2/subscription/purchase`;
      body = {
        selfServiceSubscriptionId: parameters.selfServiceSubscriptionId,
        userPaymentMethodId: parameters.paymentMethodId,
      };
    } else {
      url = `/customer/subscription/${parameters.subscriptionId}/purchase`;
      body = {
        paymentMethodId: parameters.paymentMethodId,
      };
    }

    const response = await apiFetch<Subscription | SelfServiceMembership>(url, {
      method: 'POST',
      body,
    });

    if (response && response.success) {
      return response;
    }
    throw new Error(response.message || 'Failed to purchase for subscription');
  },
});

/**
 * @deprecated Use useCancelSubscription from @m/hooks/data/customer/subscriptions instead.
 */
export const cancelSubscription = createEffect({
  handler: ({
    subscriptionId,
    subscriptionCancellationReasonId,
  }: {
    subscriptionCancellationReasonId?: number;
    subscriptionId: number;
  }) =>
    apiFetch(`/customer/subscription/${subscriptionId}`, {
      method: 'DELETE',
      body: {
        subscriptionCancellationReasonId,
      },
    }),
});
